/**
 * maplibre-gl v5 cannot be bundled by this project's webpack 4 (Vue CLI 4.5)
 * toolchain, and MapLibre advises against transpiling it, so it is loaded from
 * CDN at runtime — and only when VUE_APP_MAP_CLIENT=HUDHUD actually selects this
 * client, so Google builds never pull it in.
 * Positions crossing the boundary are always { lat, lng }; MapLibre uses
 * [lng, lat] internally, so every position is converted at the edge.
 */

const MAPLIBRE_VERSION = "5.23.0";
const MAPLIBRE_JS = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`;
const MAPLIBRE_CSS = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`;

const toLngLat = (p) => [p.lng, p.lat];

function loadCss(href) {
  return new Promise((resolve) => {
    if (document.querySelector(`link[href="${href}"]`)) return resolve();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.maplibregl) return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function buildStyleUrl() {
  const key = process.env.VUE_APP_HUDHUD_API_KEY;
  const mapId = process.env.VUE_APP_HUDHUD_MAP_ID;
  const variant = process.env.VUE_APP_HUDHUD_VARIANT || "light";
  const lang = process.env.VUE_APP_HUDHUD_LANG || "en";

  if (!key || !mapId) {
    console.warn(
      "[maps] Hudhud selected but VUE_APP_HUDHUD_API_KEY / VUE_APP_HUDHUD_MAP_ID are not set."
    );
  }

  const params = new URLSearchParams({ variant, lang, api_key: key || "" });
  return `https://b.hudhud.sa/v1/maps/styles/${mapId}?${params.toString()}`;
}

export default class HudhudMapClient {
  constructor() {
    this.maplibregl = null;
    this._loadPromise = null;
  }

  load() {
    if (!this._loadPromise) {
      this._loadPromise = Promise.all([
        loadScript(MAPLIBRE_JS),
        loadCss(MAPLIBRE_CSS),
      ]).then(() => {
        this.maplibregl = window.maplibregl;
        if (!this.maplibregl) {
          throw new Error("[maps] maplibre-gl did not load from CDN.");
        }
        return this;
      });
    }
    return this._loadPromise;
  }

  createMap(container, { center, zoom } = {}) {
    const el =
      typeof container === "string"
        ? document.getElementById(container)
        : container;

    const map = new this.maplibregl.Map({
      container: el,
      style: buildStyleUrl(),
      center: center ? toLngLat(center) : [45.0792, 23.8859],
      zoom: typeof zoom === "number" ? zoom : 5,
      attributionControl: true,
      fadeDuration: 0,
    });

    const handle = new HudhudMapHandle(this.maplibregl, map);

    // Resizing once the map has loaded, plus a ResizeObserver on the container,
    // keeps the canvas matched to the element so every visible tile gets drawn.
    map.once("load", () => map.resize());

    if (typeof ResizeObserver !== "undefined" && el) {
      let raf = null;
      const ro = new ResizeObserver(() => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          raf = null;
          handle._resizeRaf = null;
          map.resize();
        });
        handle._resizeRaf = raf;
      });
      ro.observe(el);
      handle._resizeObserver = ro;
    }

    return handle;
  }
}

class HudhudMapHandle {
  constructor(maplibregl, map) {
    this.maplibregl = maplibregl;
    this.map = map;
  }

  _imgEl(iconUrl, size) {
    const img = document.createElement("img");
    img.src = iconUrl;
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    img.style.objectFit = "contain";
    img.style.transformOrigin = "center";
    img.style.willChange = "transform";
    return img;
  }

  addIconMarker({ position, iconUrl, size = 40, zIndex }) {
    const el = this._imgEl(iconUrl, size);
    if (typeof zIndex === "number") el.style.zIndex = String(zIndex);
    const marker = new this.maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat(toLngLat(position))
      .addTo(this.map);
    return new HudhudIconMarker(marker, el);
  }

  addCaptainMarker({ position, iconUrl, size = 52 }) {
    const el = this._imgEl(iconUrl, size);
    el.style.zIndex = "10";
    const marker = new this.maplibregl.Marker({
      element: el,
      anchor: "center",
      rotationAlignment: "viewport",
    })
      .setLngLat(toLngLat(position))
      .addTo(this.map);
    return new HudhudCaptainMarker(marker, el);
  }

  fitBounds(positions, { maxZoom, padding = 40 } = {}) {
    const pts = positions || [];
    if (!pts.length) return;
    const bounds = new this.maplibregl.LngLatBounds();
    pts.forEach((p) => bounds.extend(toLngLat(p)));
    this.map.fitBounds(bounds, {
      padding,
      ...(typeof maxZoom === "number" ? { maxZoom } : {}),
      animate: false,
    });
  }

  setCenter(position) {
    this.map.setCenter(toLngLat(position));
  }

  setZoom(zoom) {
    this.map.setZoom(zoom);
  }

  getZoom() {
    return this.map.getZoom();
  }

  panTo(position) {
    this.map.panTo(toLngLat(position));
  }

  resize() {
    this.map.resize();
  }

  /** Tear down the map and stop observing the container. */
  destroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._resizeRaf) {
      cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = null;
    }
    this.map.remove();
  }
}

class HudhudIconMarker {
  constructor(marker, el) {
    this.marker = marker;
    this.el = el;
  }

  setIcon(iconUrl) {
    this.el.src = iconUrl;
  }

  remove() {
    this.marker.remove();
  }
}

class HudhudCaptainMarker {
  constructor(marker, el) {
    this.marker = marker;
    this.el = el;
  }

  getPosition() {
    const ll = this.marker.getLngLat();
    return { lat: ll.lat, lng: ll.lng };
  }

  setPosition(position) {
    this.marker.setLngLat(toLngLat(position));
  }

  setIcon(iconUrl) {
    this.el.src = iconUrl;
  }

  setRotation(deg) {
    this.marker.setRotation(deg);
  }

  remove() {
    this.marker.remove();
  }
}
